import * as React from "react";
import { useState } from "react";
import { Clock, ChevronUp, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

function LithuanianTimePicker({
  value,
  onChange,
  className,
  placeholder = "Pasirinkti laiką",
  ...props
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [hours, setHours] = useState(value ? parseInt(value.split(':')[0]) : 12);
  const [minutes, setMinutes] = useState(value ? parseInt(value.split(':')[1]) : 0);

  const formatTime = (h, m) => {
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  const incrementHours = () => setHours(h => (h + 1) % 24);
  const decrementHours = () => setHours(h => (h - 1 + 24) % 24);
  const incrementMinutes = () => setMinutes(m => (m + 5) % 60);
  const decrementMinutes = () => setMinutes(m => (m - 5 + 60) % 60);

  const handleHoursChange = (e) => {
    const val = e.target.value;
    // Allow empty input while typing
    if (val === '') {
      setHours(0);
      return;
    }
    // Only allow digits
    const numericVal = val.replace(/\D/g, '');
    if (numericVal === '') return;
    
    const num = parseInt(numericVal, 10);
    // Clamp to valid hour range
    if (num >= 0 && num <= 23) {
      setHours(num);
    } else if (num > 23) {
      setHours(23);
    }
  };

  const handleMinutesChange = (e) => {
    const val = e.target.value;
    // Allow empty input while typing
    if (val === '') {
      setMinutes(0);
      return;
    }
    // Only allow digits
    const numericVal = val.replace(/\D/g, '');
    if (numericVal === '') return;
    
    const num = parseInt(numericVal, 10);
    // Clamp to valid minute range
    if (num >= 0 && num <= 59) {
      setMinutes(num);
    } else if (num > 59) {
      setMinutes(59);
    }
  };

  const handleClear = () => {
    setHours(12);
    setMinutes(0);
    onChange('');
    setIsOpen(false);
  };

  const handleCancel = () => {
    if (value) {
      setHours(parseInt(value.split(':')[0]));
      setMinutes(parseInt(value.split(':')[1]));
    } else {
      setHours(12);
      setMinutes(0);
    }
    setIsOpen(false);
  };

  const handleSet = () => {
    onChange(formatTime(hours, minutes));
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal bg-input border-border",
            !value && "text-muted-foreground",
            className
          )}
          {...props}
        >
          <Clock className="mr-2 h-4 w-4" />
          {value || placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-4 bg-slate-900 border-indigo-500/30" align="start">
        <div className="flex flex-col gap-4">
          {/* Time Display */}
          <div className="text-center text-2xl font-bold text-amber-400">
            {formatTime(hours, minutes)}
          </div>

          {/* Time Selectors with Manual Input */}
          <div className="flex justify-center items-center gap-4">
            {/* Hours */}
            <div className="flex flex-col items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={incrementHours}
                className="h-8 w-12 text-foreground/70 hover:text-foreground"
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={hours}
                onChange={handleHoursChange}
                onFocus={(e) => e.target.select()}
                className="h-10 w-12 text-center text-xl font-semibold text-foreground bg-slate-800 rounded border-0 focus:ring-2 focus:ring-amber-500 focus:outline-none"
                maxLength={2}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={decrementHours}
                className="h-8 w-12 text-foreground/70 hover:text-foreground"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
              <span className="text-xs text-muted-foreground">Val.</span>
            </div>

            <span className="text-2xl font-bold text-foreground/50">:</span>

            {/* Minutes */}
            <div className="flex flex-col items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={incrementMinutes}
                className="h-8 w-12 text-foreground/70 hover:text-foreground"
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={minutes}
                onChange={handleMinutesChange}
                onFocus={(e) => e.target.select()}
                className="h-10 w-12 text-center text-xl font-semibold text-foreground bg-slate-800 rounded border-0 focus:ring-2 focus:ring-amber-500 focus:outline-none"
                maxLength={2}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={decrementMinutes}
                className="h-8 w-12 text-foreground/70 hover:text-foreground"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
              <span className="text-xs text-muted-foreground">Min.</span>
            </div>
          </div>

          {/* Action Buttons - Lithuanian */}
          <div className="flex justify-between items-center pt-3 border-t border-slate-700/50">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="text-muted-foreground hover:text-foreground text-xs"
            >
              Išvalyti
            </Button>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                className="text-muted-foreground hover:text-foreground text-xs"
              >
                Atšaukti
              </Button>
              <Button
                size="sm"
                onClick={handleSet}
                className="bg-primary hover:bg-primary/90 text-xs"
              >
                Nustatyti
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

LithuanianTimePicker.displayName = "LithuanianTimePicker";

export { LithuanianTimePicker };
